import React, { useEffect } from 'react'
import { Button, Form, Modal } from 'antd'
import { useTranslation } from 'react-i18next'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { useDimensionGridActions } from '../hooks/useDimensionGridActions'
import DimensionFormFields, { DimensionFormValues } from './DimensionFormFields'

export type DimensionFormMode = 'create' | 'addChild' | 'rename'

interface DimensionFormModalProps {
  open: boolean
  mode: DimensionFormMode
  dimension?: Dimension
  parentDimensionId?: string
  flatList: Dimension[]
  onClose: () => void
}

const DimensionFormModal: React.FC<DimensionFormModalProps> = ({
  open,
  mode,
  dimension,
  parentDimensionId,
  flatList,
  onClose
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<DimensionFormValues>()
  const {
    create,
    rename,
    setDefault,
    applyParentDimension,
    applyDefault,
    isCreating,
    isRenaming,
    isSettingDefault,
    isReordering
  } = useDimensionGridActions()

  const excludeIds = mode === 'rename' && dimension ? [dimension.dimensionId] : []

  useEffect(() => {
    if (!open) return
    if (mode === 'rename' && dimension) {
      form.setFieldsValue({
        name: dimension.name,
        parentDimensionId: dimension.parentDimensionId ?? undefined,
        default: !!dimension.default
      })
    } else {
      form.setFieldsValue({
        name: '',
        parentDimensionId: mode === 'addChild' ? parentDimensionId : undefined,
        default: false
      })
    }
  }, [open, mode, dimension, parentDimensionId, form])

  const handleOk = async () => {
    const values = await form.validateFields()
    if (mode === 'rename' && dimension) {
      await rename(dimension, values.name)
      await applyParentDimension(flatList, dimension, values.parentDimensionId || null)
      await applyDefault(dimension, !!values.default)
    } else {
      const result = await create({ name: values.name, parentDimensionId: values.parentDimensionId || undefined })
      if (values.default) await setDefault(result.dimensionId)
    }
    onClose()
  }

  return (
    <Modal
      maskClosable={false}
      title={mode === 'rename' ? t('dimensionsPage:edit-dimension') : t('dimensionsPage:add-new-dimension')}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('global:cancel')}
        </Button>,
        <Button
          key="ok"
          type="primary"
          onClick={handleOk}
          loading={isCreating || isRenaming || isSettingDefault || isReordering}
          style={{ marginLeft: 8 }}
        >
          {mode === 'rename' ? t('global:edit') : t('global:save')}
        </Button>
      ]}
    >
      <Form layout="vertical" form={form}>
        <DimensionFormFields flatList={flatList} excludeIds={excludeIds} isSettingDefault={isSettingDefault} />
      </Form>
    </Modal>
  )
}

export default DimensionFormModal
